import React, { useMemo } from 'react'
import styles from '../Viewer.module.scss'
import { CardTextBlock } from '../../components/cards/CardTextBlock'
import { useCommonTheme, useCurrentTheme } from '../../hooks/useCurrentTheme'
import { useSelector } from 'react-redux'
import { getValueWithSigh, processActionName, toFixed } from '../../helpers/helpers'
import { selectPreferences, selectView } from '../../Redux/selectors/selectors'
import { constants, DETAIL_MATRIX_INFO, detailInfo } from '../viewModeConstants'
import { shadowVariable } from '../utils'

const Actions = ({ actions, maxFontSize, filter, textShadow, hasAdditionalValue = false }) => {
	if (!actions || !actions.length) {
		return <></>
	}

	const {
		viewer: { mainMatrixValue },
	} = useSelector(selectPreferences)

	return actions.map(({ title, value, id, ev }) => {
		if (isNaN(value)) {
			return
		}

		let opacityValue

		if (filter.length > 0) {
			opacityValue = filter.includes(id) ? 1 : 0.4
		} else {
			opacityValue = 1
		}

		return (
			<div
				key={id}
				className={!hasAdditionalValue ? styles.actionsContainer : styles.actionsContainerWithValue}
				style={{ opacity: opacityValue, textShadow }}
			>
				<div
					style={{
						fontSize: maxFontSize,
						lineHeight: maxFontSize,
						whiteSpace: 'nowrap',
					}}
				>
					{processActionName(title)}
				</div>

				{hasAdditionalValue && (
					<div style={{ fontSize: maxFontSize, lineHeight: maxFontSize }} className={styles.valueItemAddtional}>
						{mainMatrixValue === constants.EV && getValueWithSigh(ev?.toFixed(2))}
					</div>
				)}
				<div style={{ fontSize: maxFontSize, lineHeight: maxFontSize }} className={styles.valueItem}>
					{(value * 100)?.toFixed(2)}
				</div>
			</div>
		)
	})
}

const StrategyMatrixText = ({
	cards,
	actions,
	played = null,
	field,
	detailType,
	filter,
	isMulti = false,
	detailedMatrixDimensions,
	matrixSizeX,
	matrixSizeY,
}) => {
	const {
		otherSettings: { isDetailMatrixInfo, countCell },
	} = useSelector(selectPreferences)
	const theme = useCurrentTheme()
	const { matrix } = useCommonTheme()
	const { viewModeOnePlayer, viewer_request_query, isRequest } = useSelector(selectView)
	const { textcolor1playermatrix } = theme.matrix.substrate
	const { rounding, shadow, fontSize1player, table, queryLine, equityLine, potPositionsLine } = matrix
	const {
		viewer: { mainMatrixValue },
	} = useSelector(selectPreferences)

	const fontColor = textcolor1playermatrix
	const textShadow = shadowVariable[shadow]
	const fontSize = fontSize1player

	// FOR ADDED EQ OR EQR IN DETAIL MATRIX
	const isUseAddedEq =
		!isRequest &&
		viewModeOnePlayer === constants.STRATEGY &&
		!isMulti &&
		isDetailMatrixInfo &&
		((mainMatrixValue === constants.EQR && viewer_request_query.includes('_F.')) || mainMatrixValue === constants.EQ)

	if (isUseAddedEq) {
		detailType = DETAIL_MATRIX_INFO.ACTIONS_WITH_VALUE
	}

	// FOR ADDED EV IN DETAIL MATRIX
	const hasAdditionalEv =
		viewModeOnePlayer === constants.STRATEGY && !isMulti && isDetailMatrixInfo && mainMatrixValue === constants.EV

	if (isMulti) {
		const uniqueActions = [...new Set(actions.map((action) => action.id))]

		actionsList = uniqueActions.map((uniqueAction) => actions.find((action) => action.id === uniqueAction))
		actions = actionsList
	}

	const actionsFontSize = useMemo(() => {
		if (!detailedMatrixDimensions.height || !detailedMatrixDimensions.width) {
			return `0px`
		} else {
			const countActions = detailType === DETAIL_MATRIX_INFO.ACTIONS_WITH_VALUE ? actions.length + 1 : actions.length

			const singleCellHeight = detailedMatrixDimensions.height / matrixSizeX
			const singleCellWidth = detailedMatrixDimensions.width / matrixSizeY
			let divider = matrixSizeX
			let heightCompute = detailedMatrixDimensions.height

			if (singleCellHeight > singleCellWidth) {
				heightCompute = detailedMatrixDimensions.width
				divider = matrixSizeY
			}

			// add check for width
			let fSize = (heightCompute / divider - 25) / countActions
			const charWidth = hasAdditionalEv ? singleCellWidth / 12.5 : singleCellWidth / 8.5
			fSize = charWidth < fSize ? charWidth : fSize

			return fSize + 'px'
		}
	}, [
		detailedMatrixDimensions.height,
		detailedMatrixDimensions.width,
		queryLine,
		equityLine,
		potPositionsLine,
		table,
		countCell,
		detailType,
		cards,
		actions?.length,
		matrixSizeX,
		matrixSizeY,
		hasAdditionalEv,
	])

	let actionsList

	const cardDataShow = []

	if (cards) {
		cardDataShow.push(cards.slice(0, 2))
		cardDataShow.push(cards.slice(2))
	}

	const fontSizePercentMapper = {
		0: '22px',
		1: '26px',
		2: '30px',
	}

	let value

	if (isUseAddedEq) {
		field = mainMatrixValue
	}

	switch (field) {
		case detailInfo.EV: {
			value =
				(Number(parseFloat(played.toString()).toFixed(3)) > 0 ? '+' : '') +
				parseFloat((played * 100).toString()).toFixed(2)
			break
		}
		case detailInfo.EQR:
		case detailInfo.EQ: {
			value = Number((played * 100).toFixed(2)) < 0.001 ? '0%' : `${parseFloat((played * 100).toFixed(0))}%`
			break
		}
		case detailInfo.STRATEGY: {
			value = played < 0.001 ? '0%' : `${toFixed(played * 100, 0)}%`
			break
		}
		case detailInfo.NONE: {
			value = null
			break
		}
	}

	let content
	let classNameContainer

	switch (detailType) {
		case DETAIL_MATRIX_INFO.ACTIONS: {
			classNameContainer = `${styles.actionsSvgCell}`
			content = (
				<Actions
					actions={actions}
					maxFontSize={actionsFontSize}
					filter={filter.activeActions}
					textShadow={textShadow}
					hasAdditionalValue={hasAdditionalEv}
				/>
			)
			break
		}
		case DETAIL_MATRIX_INFO.VALUE: {
			classNameContainer = `${styles.textSvgCell} ${styles.cellDataContainer}`
			content = (
				<div
					className={styles.valueContainer}
					style={{
						color: fontColor,
						borderRadius: rounding ? '2px' : 0,
						fontSize: fontSizePercentMapper[fontSize],
						textShadow,
					}}
				>
					{value}
				</div>
			)
			break
		}
		case DETAIL_MATRIX_INFO.ACTIONS_WITH_VALUE: {
			classNameContainer = `${styles.actionsSvgCell}`
			content = (
				<div className={styles.mixedContainer}>
					<div>
						<Actions
							actions={actions}
							maxFontSize={actionsFontSize}
							filter={filter.activeActions}
							textShadow={textShadow}
						/>
					</div>
					<div
						className={styles.valueContainer}
						style={{
							color: fontColor,
							borderRadius: rounding ? '2px' : 0,
							fontSize: actionsFontSize,
							lineHeight: actionsFontSize,
							textShadow,
						}}
					>
						{isUseAddedEq && (mainMatrixValue === detailInfo.EQ ? 'Eq: ' : 'EqR: ')}
						{value}
					</div>
				</div>
			)
			break
		}
	}

	return (
		<div className={classNameContainer} style={{ color: fontColor }}>
			{actions?.length > 0 && cardDataShow?.length > 0 && (
				<>
					<div className={styles.cardsContainer}>
						<CardTextBlock cards={cardDataShow.join('')} isSquare isAdaptive />
						<div className={hasAdditionalEv ? styles.valuesContainerWithEv : styles.valuesContainer}>
							{hasAdditionalEv && (
								<div className={styles.percentContainer} style={{ textShadow }}>
									{mainMatrixValue === constants.EV && 'Ev'}
								</div>
							)}
							{(viewModeOnePlayer === constants.STRATEGY || viewModeOnePlayer === constants.EQ) &&
								isDetailMatrixInfo && (
									<div className={styles.percentContainer} style={{ textShadow }}>
										%
									</div>
								)}
						</div>
					</div>
					{isDetailMatrixInfo && content}
				</>
			)}
		</div>
	)
}

export default StrategyMatrixText
